import { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import {
  Plus,
  Edit2,
  Trash2,
  Eye,
  Save,
  X,
  Image as ImageIcon,
  Upload,
  Loader2,
  FileText,
  Calendar,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", excerpt: "", category: "news", status: "draft", tags: [] });
  const [uploading, setUploading] = useState(false);
  const imgInputRef = useRef(null);

  useEffect(() => { fetchPosts(); }, []);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  async function fetchPosts() {
    try {
      const res = await fetch(`${API_URL}/api/admin/blog`, { headers });
      if (res.ok) { const d = await res.json(); setPosts(d.posts || []); }
    } catch {} finally { setLoading(false); }
  }

  async function handleSave() {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.content.trim()) { toast.error("Content is required"); return; }
    try {
      const url = editing ? `${API_URL}/api/admin/blog/${editing}` : `${API_URL}/api/admin/blog`;
      const method = editing ? "PUT" : "POST";
      const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
      if (res.ok) {
        toast.success(editing ? "Post updated" : "Post created");
        setEditing(null);
        setForm({ title: "", content: "", excerpt: "", category: "news", status: "draft", tags: [] });
        fetchPosts();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save");
      }
    } catch { toast.error("Save failed"); }
  }

  async function handleDelete(postId) {
    if (!window.confirm("Delete this post?")) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/blog/${postId}`, { method: "DELETE", headers });
      if (res.ok) { toast.success("Deleted"); fetchPosts(); }
    } catch { toast.error("Delete failed"); }
  }

  async function handleImageUpload(postId, file) {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API_URL}/api/admin/blog/${postId}/upload-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) { toast.success("Image uploaded"); fetchPosts(); }
      else { toast.error("Upload failed"); }
    } catch { toast.error("Upload error"); }
    finally { setUploading(false); }
  }

  function startEdit(post) {
    setEditing(post.post_id);
    setForm({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || "",
      category: post.category || "news",
      status: post.status || "draft",
      tags: post.tags || [],
    });
  }

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6" data-testid="blog-manager">
      {/* Editor */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-600" />
          {editing ? "Edit Post" : "Create New Post"}
        </h3>
        <div className="space-y-4">
          <div>
            <Label className="text-sm text-gray-700">Title</Label>
            <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Post title..." data-testid="blog-title-input" />
          </div>
          <div>
            <Label className="text-sm text-gray-700">Excerpt (optional, auto-generated from content)</Label>
            <Input value={form.excerpt} onChange={e => setForm(p => ({ ...p, excerpt: e.target.value }))} placeholder="Brief summary..." />
          </div>
          <div>
            <Label className="text-sm text-gray-700">Content</Label>
            <textarea
              className="w-full min-h-[200px] p-3 border border-gray-300 rounded-lg text-sm resize-y focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={form.content}
              onChange={e => setForm(p => ({ ...p, content: e.target.value }))}
              placeholder="Write your post content here..."
              data-testid="blog-content-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-gray-700">Category</Label>
              <select className="w-full p-2 border border-gray-300 rounded-lg text-sm" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                <option value="news">News</option>
                <option value="scouting">Scouting</option>
                <option value="combine">Combine</option>
                <option value="success-story">Success Story</option>
                <option value="announcement">Announcement</option>
              </select>
            </div>
            <div>
              <Label className="text-sm text-gray-700">Status</Label>
              <select className="w-full p-2 border border-gray-300 rounded-lg text-sm" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} data-testid="blog-status-select">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="blog-save-btn">
              <Save className="w-4 h-4 mr-2" /> {editing ? "Update" : "Create"} Post
            </Button>
            {editing && (
              <Button variant="outline" onClick={() => { setEditing(null); setForm({ title: "", content: "", excerpt: "", category: "news", status: "draft", tags: [] }); }}>
                <X className="w-4 h-4 mr-2" /> Cancel
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Posts List */}
      <div className="space-y-3">
        <h3 className="text-lg font-bold text-gray-900">All Posts ({posts.length})</h3>
        {posts.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
            <FileText className="w-8 h-8 mx-auto text-gray-300 mb-2" />
            <p>No blog posts yet. Create your first one above!</p>
          </div>
        ) : (
          posts.map(post => (
            <div key={post.post_id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-start gap-4" data-testid={`blog-post-${post.post_id}`}>
              {post.featured_image_url ? (
                <img src={`${API_URL}${post.featured_image_url}`} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
              ) : (
                <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <ImageIcon className="w-6 h-6 text-gray-300" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-gray-900 truncate">{post.title}</h4>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${post.status === "published" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                    {post.status}
                  </span>
                </div>
                <p className="text-sm text-gray-500 line-clamp-1">{post.excerpt || post.content?.substring(0, 100)}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(post.created_at).toLocaleDateString()}</span>
                  <span>{post.category}</span>
                  <span>{post.views || 0} views</span>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <input type="file" ref={imgInputRef} className="hidden" accept="image/*"
                  onChange={e => { if (e.target.files[0]) handleImageUpload(post.post_id, e.target.files[0]); }} />
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { imgInputRef.current?.click(); }}
                  disabled={uploading} title="Upload image">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 text-gray-500" />}
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => startEdit(post)} title="Edit">
                  <Edit2 className="w-4 h-4 text-blue-500" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(post.post_id)} title="Delete">
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export function SocialSlideManager() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", caption: "", link_url: "", platform: "general", order: 0 });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  useEffect(() => { fetchSlides(); }, []);

  async function fetchSlides() {
    try {
      const res = await fetch(`${API_URL}/api/admin/social-slides`, { headers });
      if (res.ok) { const d = await res.json(); setSlides(d.slides || []); }
    } catch {} finally { setLoading(false); }
  }

  async function handleCreate() {
    if (!form.title.trim() && !form.caption.trim()) { toast.error("Title or caption required"); return; }
    try {
      const res = await fetch(`${API_URL}/api/admin/social-slides`, { method: "POST", headers, body: JSON.stringify(form) });
      if (res.ok) {
        const data = await res.json();
        toast.success("Slide created! Now upload an image for it.");
        setForm({ title: "", caption: "", link_url: "", platform: "general", order: slides.length });
        fetchSlides();
      }
    } catch { toast.error("Create failed"); }
  }

  async function handleImageUpload(slideId, file) {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API_URL}/api/admin/social-slides/${slideId}/upload-image`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData
      });
      if (res.ok) { toast.success("Image uploaded"); fetchSlides(); }
    } catch { toast.error("Upload error"); }
    finally { setUploading(false); }
  }

  async function handleDelete(slideId) {
    if (!window.confirm("Delete this slide?")) return;
    const res = await fetch(`${API_URL}/api/admin/social-slides/${slideId}`, { method: "DELETE", headers });
    if (res.ok) { toast.success("Deleted"); fetchSlides(); }
  }

  async function toggleActive(slide) {
    await fetch(`${API_URL}/api/admin/social-slides/${slide.slide_id}`, {
      method: "PUT", headers, body: JSON.stringify({ is_active: !slide.is_active })
    });
    fetchSlides();
  }

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6" data-testid="social-manager">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-pink-600" />
          Add Social Slide
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm text-gray-700">Title</Label>
            <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Slide title..." data-testid="slide-title-input" />
          </div>
          <div>
            <Label className="text-sm text-gray-700">Link URL (where to redirect)</Label>
            <Input value={form.link_url} onChange={e => setForm(p => ({ ...p, link_url: e.target.value }))} placeholder="https://instagram.com/p/..." />
          </div>
          <div className="col-span-2">
            <Label className="text-sm text-gray-700">Caption</Label>
            <Input value={form.caption} onChange={e => setForm(p => ({ ...p, caption: e.target.value }))} placeholder="Slide caption..." />
          </div>
          <div>
            <Label className="text-sm text-gray-700">Platform</Label>
            <select className="w-full p-2 border border-gray-300 rounded-lg text-sm" value={form.platform} onChange={e => setForm(p => ({ ...p, platform: e.target.value }))}>
              <option value="general">General</option>
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
              <option value="twitter">Twitter/X</option>
            </select>
          </div>
          <div>
            <Label className="text-sm text-gray-700">Order</Label>
            <Input type="number" value={form.order} onChange={e => setForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} />
          </div>
        </div>
        <Button onClick={handleCreate} className="mt-4 bg-pink-600 hover:bg-pink-700 text-white" data-testid="slide-create-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Slide
        </Button>
      </div>

      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-3">Slideshow ({slides.length} slides)</h3>
        {slides.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
            <ImageIcon className="w-8 h-8 mx-auto text-gray-300 mb-2" />
            <p>No slides yet. Create one above then upload an image.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {slides.map(slide => (
              <div key={slide.slide_id} className={`bg-white rounded-xl border shadow-sm overflow-hidden ${slide.is_active ? 'border-green-300' : 'border-gray-200 opacity-60'}`} data-testid={`slide-${slide.slide_id}`}>
                <div className="aspect-video bg-gray-100 relative">
                  {slide.image_url ? (
                    <img src={slide.image_url.startsWith('/') ? `${API_URL}${slide.image_url}` : slide.image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-10 h-10 text-gray-300" />
                    </div>
                  )}
                  <span className={`absolute top-2 right-2 px-2 py-0.5 rounded text-xs font-medium ${slide.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {slide.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="p-3">
                  <h4 className="font-semibold text-sm text-gray-900 truncate">{slide.title || "Untitled"}</h4>
                  <p className="text-xs text-gray-500 truncate">{slide.caption}</p>
                  <div className="flex items-center gap-1 mt-2">
                    <input type="file" className="hidden" ref={fileRef} accept="image/*"
                      onChange={e => { if (e.target.files[0]) handleImageUpload(slide.slide_id, e.target.files[0]); }} />
                    <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => fileRef.current?.click()} disabled={uploading}>
                      <Upload className="w-3 h-3 mr-1" /> Image
                    </Button>
                    <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => toggleActive(slide)}>
                      <Eye className="w-3 h-3 mr-1" /> {slide.is_active ? "Hide" : "Show"}
                    </Button>
                    <Button variant="ghost" size="sm" className="text-xs h-7 text-red-500" onClick={() => handleDelete(slide.slide_id)}>
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
